import { Check, Copy } from 'lucide-react-native';
import { useEffect, useRef, useState } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';

import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { TWO_SECONDS } from '@/constants/time';
import useClipboard from '@/hooks/useClipboard';
import { TOUCH_TARGET_MIN_H } from '@/util/accessibleTouchTarget';

interface CopyFieldProps {
  /** Shown above the value, and the subject of the copy control's label. */
  label: string;
  /** What reaches the clipboard. Always the whole, usable value. */
  value: string;
  /**
   * What the reader sees, when that differs from what they should receive — a
   * URL reads better without its scheme and pastes worse without it. Leave it
   * unset and the value is shown as it is copied.
   */
  displayValue?: string;
  /** One line under the value explaining what the value is for. */
  hint?: string;
  /** Runs once the value is on the clipboard. Tracking belongs to the caller. */
  onCopied?: () => void;
  className?: string;
}

/**
 * A labeled value with a copy control, for a string the reader is expected to
 * hand to someone else rather than read.
 *
 * The icon flips to a tick for two seconds after a copy, and the control's
 * accessibility label flips with it inside a polite live region, because a
 * status carried by the icon alone does not reach a screen reader.
 */
export function CopyField({
  label,
  value,
  displayValue,
  hint,
  onCopied,
  className,
}: CopyFieldProps) {
  const { copyToClipboard } = useClipboard();

  const [copied, setCopied] = useState(false);

  /**
   * Held so a second copy restarts the confirmation rather than inheriting the
   * first one's remaining time, and so leaving the screen mid-confirmation does
   * not leave a timer to wake up against a gone component.
   */
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (resetTimer.current) {
        clearTimeout(resetTimer.current);
      }
    },
    []
  );

  const handleCopy = () => {
    Promise.resolve()
      .then(() =>
        copyToClipboard(
          value,
          () => {
            setCopied(true);

            if (resetTimer.current) {
              clearTimeout(resetTimer.current);
            }

            resetTimer.current = setTimeout(() => setCopied(false), TWO_SECONDS);

            onCopied?.();
          },
          `${label} copied`
        )
      )
      .catch(() => undefined);
  };

  return (
    /**
     * The words live in their own column beside the control rather than in
     * rows around it. Stacked the other way, the label and the hint are full
     * width and a long hint runs underneath the control, which is both a
     * collision and a stray press target.
     *
     * The row never shrinks. A wrapped hint makes it taller than its one-line
     * height, and a flex parent short of room would otherwise squash it and
     * spill the hint over whatever follows.
     */
    <HStack className={twMerge('w-full shrink-0 items-center gap-3', className)}>
      <VStack className="flex-1 gap-1">
        <Text size="xs" className="font-semibold uppercase text-typography-700">
          {label}
        </Text>

        <Text selectable className="font-medium text-typography-900">
          {displayValue ?? value}
        </Text>

        {hint ? (
          <Text size="sm" className="text-typography-700">
            {hint}
          </Text>
        ) : null}
      </VStack>

      {/* The box itself is the 48px target rather than hit slop around an
          18px icon, because react-native-web ignores hitSlop entirely and
          would leave the web control the size of the glyph. */}
      <Pressable
        onPress={handleCopy}
        className={twJoin(TOUCH_TARGET_MIN_H, 'min-w-[48px] items-center justify-center')}
        accessibilityRole="button"
        accessibilityLiveRegion="polite"
        accessibilityLabel={copied ? `${label} copied` : `Copy ${label.toLowerCase()}`}>
        <Icon as={copied ? Check : Copy} size="md" className="text-typography-700" />
      </Pressable>
    </HStack>
  );
}
